import { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Typography,
} from "@mui/material";
// import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
// import CheckSharpIcon from '@mui/icons-material/CheckSharp'
// import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
// import Visibility from '@mui/icons-material/Visibility'
// import VisibilityOff from '@mui/icons-material/VisibilityOff'
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { socketEventEmitter } from "../../../../socket";
import { useProject } from "../../../../context/ProjectContext";
import ProjectService from "../../../../services/ProjectService";
// import LoginPageElements from './LoginPageElements'
// import FailedLoginDetails from './FailedLoginDetails'
// import FailedLoginStepScreenshotModal from './FailedLoginStepScreenshotModal'
import UpdateLoginCredentialsModal from "../../../modals/UpdateLoginCredentialsModal";
import LoginFailedModal from "../../../modals/LoginFailedModal";
import LoginIssueModal from "../../../modals/LoginIssueModal";
// import ElementsScreenshotModal from './ElementsScreenshotModal'
import { useNotification } from "../../../../context/NotificationContext";
import LoginStatus from "./components/LoginStatus";
import AnimatedStepIcon from "./components/AnimatedStepIcon";
import StepImages from "./components/StepImages";
import styles from "./styles.module.scss";
import LoginSuccess from "./components/LoginSuccess";
import { shouldShowTestGenerationDebug } from "utils/addExternalScripts";
import { formatDate } from "../../../../utils/formatDate";
import {
    formatTokenCount,
    formatUsdCost,
    getDiscoveryDebugSummary,
} from "../../../../utils/discoveryDebug";

const AUTOMATIC_LOGIN_ISSUE_MESSAGE =
    "Your login flow couldn't be recognized automatically. Please provide a few details to help us investigate.";
const LOGIN_FAILED_MESSAGE =
    "Login failed. Please check your credentials and try again.\nIf the issue persists, click Report issue and provide a few details to help us investigate.";
const REPORT_ISSUE_BUTTON_LABEL = "REPORT ISSUE";
const isFailureStatus = (status) =>
    status === "failed" || status === "bot_protection";

const LoginTab = ({
    // url,
    setUrl,
    // urlError,
    // handleUrlChange,
    // loginFormId,
    setLoginFormId,
    // login,
    setLogin,
    // loginError,
    // handleLoginChange,
    // isPasswordExist,
    setIsPasswordExist,
    // isEditingPassword,
    // newPassword,
    // handleNewPasswordChange,
    // handleClosePassword,
    // handleSavePassword,
    // handleEditPassword,
    // password,
    // handlePasswordChange,
    // passwordError,
    setElementsIdRoles,
    setSelectedSection,
    // setRescanLoginPageLoading,
    // setOpenLoginRequiredModal
}) => {
    const { selectedProject } = useProject();

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [projectLoginData, setProjectLoginData] = useState(null);
    // const [selectedFailedLoginStep, setSelectedFailedLoginStep] = useState(null)
    // const [openElementModal, setOpenElementModal] = useState(false)
    const [updateCredentialsOpen, setUpdateCredentialsOpen] = useState(false);
    const [isOTPSettingActiveForUpdate, setIsOTPSettingActiveForUpdate] =
        useState(false);
    const [loginFailedOpen, setLoginFailedOpen] = useState(false);
    const [loginIssueOpen, setLoginIssueOpen] = useState(false);
    const showNotification = useNotification();
    const baseStepsConfig = useMemo(() => {
        return [
            { label: "Handle Basic Anti-Bot Protection." },
            { label: "Close Unnecessary Popups" },
            { label: "Generating Login Flow Steps" },
            { label: "Enter Credentials" },
        ];
    }, []);
    const [stepImages, setStepImages] = useState([]);
    const [showDebug, setShowDebug] = useState(null); // controls visibility by shouldShowTestGenerationDebug
    const [generationDebug, setGenerationDebug] = useState(null); // state for debug payload

    useEffect(() => {
        (async () => {
            try {
                const allowed = await shouldShowTestGenerationDebug();
                setShowDebug(allowed);
            } catch {
                setShowDebug(false);
            }
        })();
    }, []);

    const handleReTry = async () => {
        //   setLoginFailedOpen(true);
        if (!selectedProject?.id) {
            showNotification("No project selected", "error", "");
            return;
        }

        try {
            await ProjectService.retryLogin(selectedProject.id);
            showNotification("Login retry initiated", "success", "");
            setLoginFailedOpen(false);
        } catch (error) {
            console.error("Error retrying login:", error);
            showNotification(
                error.response?.data?.message || "Failed to retry login",
                "error",
                ""
            );
        }
    };

    const handleSubmitIssue = () => {
        setLoginIssueOpen(true);
    };

    const handleUpdateCredentials = () => {
        setUpdateCredentialsOpen(true);
    };

    const handleRestartLoginSetup = () => {
        setIsOTPSettingActiveForUpdate(true);
        setUpdateCredentialsOpen(true);
    };

    const handleRestartComplete = () => {
        // Only reset the active step when restart is actually completed/saved
        setActiveStep(0);
    };

    const resetStepImages = () => {
        const newStepImages = stepImages.map((_, index) => ({
            step: index,
            image: null,
        }));
        setStepImages(newStepImages);
    };

    const handleReLogin = async () => {
        if (!selectedProject?.id) {
            showNotification("No project selected", "error", "");
            return;
        }

        try {
            await ProjectService.reLogin(selectedProject.id);
            resetStepImages();
            showNotification("Re-login initiated", "success", "");
        } catch (error) {
            console.error("Error initiating re-login:", error);
            showNotification(
                error.response?.data?.message || "Failed to initiate re-login",
                "error",
                ""
            );
        }
    };

    const isInProgress = () => {
        return steps.some((item) => item.status === "running");
    };

    // const [showPassword, setShowPassword] = useState(false)
    // const [showNewPassword, setShowNewPassword] = useState(false)
    // const [openStepModal, setOpenStepModal] = useState(false)

    const getProjectLoginData = async () => {
        try {
            const projectLoginData =
                await ProjectService.getProjectLoginDataById(
                    selectedProject.id
                );
            console.log("projectLoginData:", projectLoginData);
            const authData = await ProjectService.getAuthDataByProjectId(
                selectedProject.id
            );
            console.log("authData:", authData);
            if (authData?.discovery_debug) {
                setGenerationDebug(authData.discovery_debug); // set state instead of local var
            }
            setProjectLoginData(projectLoginData);
            setUrl(projectLoginData.login_page_url);
            setLoginFormId(projectLoginData.login_form_id);
            setLogin(projectLoginData.login);
            setIsPasswordExist(
                projectLoginData?.is_password_exist || projectLoginData.password
            );

            if (projectLoginData) {
                let currentStepsConfig = [...baseStepsConfig];
                if (projectLoginData.one_time_password) {
                    currentStepsConfig.push({
                        label: "Enter OTP and Log In",
                    });
                }

                // Create default step image array
                if (stepImages.length === 0) {
                    let newStepImages = [...stepImages];
                    newStepImages = currentStepsConfig.map((_, index) => ({
                        step: index,
                        image: null,
                    }));
                    newStepImages[projectLoginData.login_stage - 1].image =
                        projectLoginData.login_stage_screenshot;
                    setStepImages(newStepImages);
                }

                const newSteps = currentStepsConfig.map((step, index) => {
                    let status = "pending";
                    if (projectLoginData.login_stage > index + 1) {
                        status = "completed";
                    } else if (projectLoginData.login_stage === index + 1) {
                        status = projectLoginData.login_stage_status;
                    }
                    return { ...step, status };
                });
                setSteps(newSteps);
                if (projectLoginData.login_stage_status === "completed") {
                    setActiveStep(projectLoginData.login_stage);
                } else {
                    setActiveStep(projectLoginData.login_stage - 1);
                }
            }

            if (projectLoginData.elements) {
                setElementsIdRoles(
                    projectLoginData.elements.map((element) => {
                        return {
                            id: element.id,
                            role: element.role,
                        };
                    })
                );
                setSelectedSection({
                    id: projectLoginData.id,
                    name: "Login",
                    img_storage_link: projectLoginData.img_storage_link,
                    elements: projectLoginData.elements,
                });
            }
        } catch (error) {
            console.log(error);
            console.log(error?.response?.data?.error);
        }
    };

    useEffect(() => {
        getProjectLoginData();
        // eslint-disable-next-line
    }, [selectedProject.id]);

    useEffect(() => {
        const handleTaskCompleted = async (data) => {
            if (
                data.type === "Login Elements Processing" ||
                data.type === "Login Test" ||
                data.type === "reLogin"
            ) {
                getProjectLoginData();
                // setRescanLoginPageLoading(false)
            }
            if (data.discovery_debug) {
                setGenerationDebug(data.discovery_debug);
            }
        };

        const handleTaskFailed = async (data) => {
            if (
                data.type === "Login Elements Processing" ||
                data.type === "Login Test" ||
                data.type === "reLogin"
            ) {
                setProjectLoginData(null);
                getProjectLoginData();
                // setRescanLoginPageLoading(false)
            }
        };

        const handleLoginStageUpdateCompleted = async (data) => {
            const newStepImages = [...stepImages];
            const elemToUpdate = newStepImages[data.login_stage - 1];
            if (elemToUpdate && data.screenshot) {
                elemToUpdate.image = data.screenshot;
                setStepImages(newStepImages);
            }
            getProjectLoginData();
        };

        socketEventEmitter.on("taskCompleted", handleTaskCompleted);
        socketEventEmitter.on("taskFailed", handleTaskFailed);
        socketEventEmitter.on(
            "login_stage_update",
            handleLoginStageUpdateCompleted
        );

        return () => {
            socketEventEmitter.off("taskCompleted", handleTaskCompleted);
            socketEventEmitter.off("taskFailed", handleTaskFailed);
            socketEventEmitter.off(
                "login_stage_update",
                handleLoginStageUpdateCompleted
            );
        };
        // eslint-disable-next-line
    }, [stepImages]);

    useEffect(() => {
        if (activeStep === 0) {
            resetStepImages();
        }
        // eslint-disable-next-line
    }, [activeStep]);

    const renderGenerationDebug = () => {
        console.log("generationDebug:", generationDebug);
        if (!generationDebug) return null;
        const { video_url, aws_tracing_url, llm_logs = [] } = generationDebug;
        const debugSummary = getDiscoveryDebugSummary(generationDebug);
        return (
            <details style={{ marginBottom: 16 }}>
                <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                    Test generation debug
                </summary>
                <div style={{ padding: 12 }}>
                    {debugSummary ? (
                        <div
                            style={{
                                marginBottom: 12,
                                padding: 12,
                                background: "#f7f9fc",
                                border: "1px solid #e3e8ef",
                                borderRadius: 8,
                            }}
                        >
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                Gemini cost
                            </div>
                            <div>
                                Total: {formatUsdCost(debugSummary.totalUsd)}{" "}
                                {debugSummary.currency}
                            </div>
                            <div>
                                Tokens: {formatTokenCount(debugSummary.inputTokens)} in /{" "}
                                {formatTokenCount(debugSummary.outputTokens)} out
                            </div>
                            <div>
                                Priced calls: {formatTokenCount(debugSummary.pricedCalls)}
                            </div>
                        </div>
                    ) : null}
                    {video_url ? (
                        <div style={{ marginBottom: 12 }}>
                            <div>Discovery video:</div>
                            <video
                                src={video_url}
                                controls
                                style={{ width: "100%", maxWidth: 640 }}
                            />
                        </div>
                    ) : null}
                    {aws_tracing_url ? (
                        <div style={{ marginBottom: 12 }}>
                            <a
                                href={aws_tracing_url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Open trace report
                            </a>
                        </div>
                    ) : null}
                    {llm_logs.length ? (
                        <div style={{ overflowX: "auto" }}>
                            <div style={{ fontWeight: 600, margin: "8px 0" }}>
                                LLM I/O
                            </div>
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            #
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            route
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            cost (usd)
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            request json
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            request ts
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            response json
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            response ts
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            page link
                                        </th>
                                        <th
                                            style={{
                                                textAlign: "left",
                                                borderBottom: "1px solid #eee",
                                                padding: 6,
                                            }}
                                        >
                                            image
                                        </th>{" "}
                                        {/* added */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {llm_logs.map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={{ padding: 6 }}>
                                                {idx + 1}
                                            </td>
                                            <td
                                                style={{
                                                    padding: 6,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {row.route}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {formatUsdCost(row.llm_cost_usd)}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.request_json ? (
                                                    <a
                                                        href={row.request_json}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        request_json
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.request_timestamp ? formatDate(row.request_timestamp) : "-"}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.response_json ? (
                                                    <a
                                                        href={row.response_json}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        response_json
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.response_timestamp ? formatDate(row.response_timestamp) : "-"}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.page_link ? (
                                                    <a
                                                        href={row.page_link}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {row.page_link}
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td style={{ padding: 6 }}>
                                                {row.image ? (
                                                    <a
                                                        href={row.image}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        screenshot
                                                    </a>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>{" "}
                                            {/* added */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                </div>
            </details>
        );
    };

    const isCredentialsStepFailed =
        projectLoginData?.login_stage === 4 &&
        isFailureStatus(projectLoginData?.login_stage_status);

    return (
        <Box className="projectSettingsLoginTab">
            {showDebug && generationDebug ? renderGenerationDebug() : null}
            <LoginStatus
                status={projectLoginData?.login_stage_status}
                stage={projectLoginData?.login_stage}
            />
            <Box className={styles.tabGridLayout}>
                <Box>
                    <Stepper
                        className="loginTabStepper"
                        activeStep={activeStep}
                        orientation="vertical"
                    >
                        {steps.map((step, index) => {
                            const isHighlightedCredentialsFlowStep =
                                isCredentialsStepFailed && index === 2;
                            const isFailed =
                                isFailureStatus(step.status) ||
                                isHighlightedCredentialsFlowStep;
                            const isCompleted =
                                step.status === "completed" &&
                                !isHighlightedCredentialsFlowStep;
                            const isActive = !isCompleted && !isFailed;
                            const isCurrent = activeStep === index;

                            return (
                                <Step
                                    key={step.label}
                                    className="loginTabStep"
                                    expanded={true}
                                    active={isActive}
                                    completed={isCompleted}
                                >
                                    <StepLabel
                                        error={isFailed}
                                        StepIconComponent={(iconProps) => (
                                            <AnimatedStepIcon
                                                {...iconProps}
                                                isCurrent={isCurrent}
                                            />
                                        )}
                                    >
                                        {step.label}
                                    </StepLabel>
                                    <StepContent>
                                        {index === 0 ? (
                                            activeStep === 0 ? (
                                                step.status ===
                                                "running" ? null : (
                                                    <>
                                                        <Typography className="loginTabStepDescription">
                                                            {isFailureStatus(
                                                                step.status
                                                            )
                                                                ? AUTOMATIC_LOGIN_ISSUE_MESSAGE
                                                                : "Blocked by anti-bot tools. Please disable them and try again."}
                                                        </Typography>
                                                        {isFailureStatus(
                                                            step.status
                                                        ) ? (
                                                            <Button
                                                                className="loginTabStepButton updateCredentialsButton"
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={
                                                                    handleSubmitIssue
                                                                }
                                                            >
                                                                {
                                                                    REPORT_ISSUE_BUTTON_LABEL
                                                                }
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                className="loginTabStepButton"
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={
                                                                    handleReTry
                                                                }
                                                            >
                                                                RE-TRY
                                                            </Button>
                                                        )}
                                                    </>
                                                )
                                            ) : (
                                                <>
                                                    <Typography className="loginTabStepDescription">
                                                        No anti-bot mechanisms
                                                        were detected at this
                                                        step.
                                                    </Typography>
                                                    <Button
                                                        className="loginTabStepButton"
                                                        variant="contained"
                                                        color="secondary"
                                                        disabled
                                                    >
                                                        RE-TRY
                                                    </Button>
                                                </>
                                            )
                                        ) : null}
                                        {index === 1 && activeStep >= 1 ? (
                                            step.status === "running" ? null : (
                                                <>
                                                    <Typography className="loginTabStepDescription">
                                                        {isFailureStatus(step.status)
                                                            ? AUTOMATIC_LOGIN_ISSUE_MESSAGE
                                                            : "We automatically dismissed cookie banners, modals, and overlays that could block interaction."}
                                                    </Typography>
                                                    {isFailureStatus(step.status) ? (
                                                        <Button
                                                            className="loginTabStepButton updateCredentialsButton"
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={
                                                                handleSubmitIssue
                                                            }
                                                        >
                                                            {
                                                                REPORT_ISSUE_BUTTON_LABEL
                                                            }
                                                        </Button>
                                                    ) : null}
                                                </>
                                            )
                                        ) : null}
                                        {index === 2 ? (
                                            activeStep >= 2 &&
                                            !isHighlightedCredentialsFlowStep ? (
                                                step.status ===
                                                "running" ? null : (
                                                    <>
                                                        <Typography className="loginTabStepDescription">
                                                            {isFailureStatus(
                                                                step.status
                                                            )
                                                                ? AUTOMATIC_LOGIN_ISSUE_MESSAGE
                                                                : "Login inputs were successfully located."}
                                                        </Typography>
                                                        {isFailureStatus(
                                                            step.status
                                                        ) ? (
                                                            <Button
                                                                className="loginTabStepButton updateCredentialsButton"
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={
                                                                    handleSubmitIssue
                                                                }
                                                            >
                                                                {
                                                                    REPORT_ISSUE_BUTTON_LABEL
                                                                }
                                                            </Button>
                                                        ) : null}
                                                    </>
                                                )
                                            ) : null
                                        ) : null}
                                        {index === 3 ? (
                                            activeStep >= 3 ? (
                                                step.status ===
                                                "running" ? null : (
                                                    <>
                                                        <Typography
                                                            className="loginTabStepDescription"
                                                            sx={{
                                                                whiteSpace:
                                                                    "pre-line",
                                                            }}
                                                        >
                                                            {isFailureStatus(
                                                                step.status
                                                            )
                                                                ? LOGIN_FAILED_MESSAGE
                                                                : "Credentials were entered. If your credentials have changed, please update them."}
                                                        </Typography>
                                                        <Box className="loginTabStepActions">
                                                            <Button
                                                                className="loginTabStepButton updateCredentialsButton"
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={
                                                                    handleUpdateCredentials
                                                                }
                                                            >
                                                                UPDATE CREDENTIALS
                                                            </Button>
                                                            {isFailureStatus(
                                                                step.status
                                                            ) ? (
                                                                <Button
                                                                    className="loginTabStepButton updateCredentialsButton"
                                                                    variant="contained"
                                                                    color="secondary"
                                                                    onClick={
                                                                        handleSubmitIssue
                                                                    }
                                                                >
                                                                    {
                                                                        REPORT_ISSUE_BUTTON_LABEL
                                                                    }
                                                                </Button>
                                                            ) : null}
                                                        </Box>
                                                    </>
                                                )
                                            ) : null
                                        ) : null}
                                        {index === 4 && activeStep >= 4 ? (
                                            step.status === "running" ? null : (
                                                <>
                                                    <Typography className="loginTabStepDescription">
                                                        {step.status ===
                                                        "failed"
                                                            ? "A One-Time Password (OTP) wrong or not entered. Please try to RE-LOGIN."
                                                            : "A One-Time Password (OTP) was successfully requested and entered. Login completed."}
                                                    </Typography>
                                                    <Button
                                                        className="loginTabStepButton reLoginButton"
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={handleReLogin}
                                                        disabled={
                                                            !projectLoginData?.steps ||
                                                            projectLoginData
                                                                .steps
                                                                .length === 0
                                                        }
                                                    >
                                                        RE-LOGIN
                                                    </Button>
                                                    {isFailureStatus(
                                                        step.status
                                                    ) ? (
                                                        <Button
                                                            className="loginTabStepButton updateCredentialsButton"
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={
                                                                handleSubmitIssue
                                                            }
                                                        >
                                                            {
                                                                REPORT_ISSUE_BUTTON_LABEL
                                                            }
                                                        </Button>
                                                    ) : null}
                                                </>
                                            )
                                        ) : null}
                                    </StepContent>
                                </Step>
                            );
                        })}
                    </Stepper>
                    {/* {activeStep >= steps.length - 1 && ( */}
                    <LoginSuccess
                        isLoggedIn={projectLoginData?.login_stage_status}
                    />
                    <Box className="loginTabRestartContainer">
                        <Typography className="loginTabRestartTitle">
                            Need to Re-login?
                        </Typography>
                        <Typography className="loginTabRestartDescription">
                            If you need to renew your auth session, use the
                            Re-login button.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                                className="loginTabRestartButton"
                                variant="outlined"
                                color="secondary"
                                onClick={handleReLogin}
                                disabled={
                                    !projectLoginData?.steps ||
                                    projectLoginData.steps.length === 0 ||
                                    isInProgress()
                                }
                            >
                                RE-LOGIN
                            </Button>
                        </Box>
                    </Box>
                    {/* )} */}
                    <LoginIssueModal
                        open={loginIssueOpen}
                        setOpen={setLoginIssueOpen}
                        projectId={selectedProject?.id}
                        initialLogin={projectLoginData?.login}
                    />
                    <UpdateLoginCredentialsModal
                        updateCredentialsOpen={updateCredentialsOpen}
                        setUpdateCredentialsOpen={setUpdateCredentialsOpen}
                        isOTPSettingActiveForUpdate={
                            isOTPSettingActiveForUpdate
                        }
                        setIsOTPSettingActiveForUpdate={
                            setIsOTPSettingActiveForUpdate
                        }
                        onRestartComplete={handleRestartComplete}
                    />
                    <LoginFailedModal
                        loginFailedOpen={loginFailedOpen}
                        setLoginFailedOpen={setLoginFailedOpen}
                    />
                </Box>
                <Box className={styles.rightColumnContainer}>
                    <StepImages steps={stepImages} />
                    <Box className="loginTabRestartContainer">
                        <Typography className="loginTabRestartTitle">
                            Need to Restart?
                        </Typography>
                        <Typography className="loginTabRestartDescription">
                            If your login flow has changed significantly, you
                            can restart the setup from the beginning.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Button
                                className="loginTabRestartButton"
                                variant="contained"
                                color="secondary"
                                onClick={handleRestartLoginSetup}
                                disabled={isInProgress()}
                            >
                                Restart Login Setup
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginTab;
